import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getApprovedWorksByType } from "../../services/AppService";
import closeButton from "../../assets/images/close.png";
import { useTranslation } from "react-i18next";
import PageFade from "../../components/Login/PageFade";

const WorkCompletionList = () => {
    const { workTypeId } = useParams();
    const { state } = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();



    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(0); // Reset to first page on new search
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchWorks = useCallback(async () => {
        if (!workTypeId || workTypeId === "undefined") return;

        setLoading(true);
        try {
            // Using debouncedSearch, page, and size for dynamic filtering
            const res = await getApprovedWorksByType(workTypeId, debouncedSearch, page, size);
            if (res) {
                setData(res.content || []);
                setTotalPages(res.totalPages || 0);
                setTotalElements(res.totalElements || 0);
            }
        } catch (err) {
            console.error("Failed to fetch works", err);
        } finally {
            setLoading(false);
        }
    }, [workTypeId, debouncedSearch, page, size]);

    useEffect(() => {
        fetchWorks();
    }, [fetchWorks]);

    const handleMarkComplete = (item) => {
        navigate(`/smartcity/works-completion/works-for-completion/add-completion/${item.id}`, {
            state: { workTitle: item.workTitle }
        });
    };

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-6">
                {/* Header */}
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h4 className="text-xl font-semibold text-blue uppercase">
                        {t('pageTitles.worksCompletionList') || "Add / Edit Work Completed Details"} : {state?.workTypeName || ""}
                    </h4>
                    <img
                        src={closeButton}
                        alt="Close"
                        className="w-6 h-6 cursor-pointer"
                        onClick={() => navigate(-1)}
                    />
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                    <div className="flex items-center gap-2 text-sm">
                        <label className="text-gray-600">{t('common.show')}:</label>
                        <select
                            value={size}
                            onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                            className="border rounded px-2 py-1 outline-none focus:border-blue-400"
                        >
                            {[10, 25, 50, 100].map(val => <option key={val} value={val}>{val}</option>)}
                        </select>
                        <label className="text-gray-600">{t('common.entries')}</label>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            placeholder={t('common.search')}
                            className="border rounded px-3 py-1 text-sm outline-none w-full sm:w-64 focus:border-blue-400"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full border ctable rounded-lg">
                        <thead>
                            <tr className="bg-[#a0adf1] text-white">
                                <th className="px-2 py-2 text-center border-b border-black">{t('tables.srNo')}</th>
                                <th className="px-2 py-2 text-left border-b border-black">{t('work.workName')}</th>
                                <th className="px-2 py-2 text-left border-b border-black">{t('work.tenderId')}</th>
                                <th className="px-2 py-2 text-left border-b border-black">{t('work.workOrderNo')}</th>
                                <th className="px-2 py-2 text-left border-b border-black">{t('work.bidderName')}</th>
                                <th className="px-2 py-2 text-right border-b border-black">{t('work.bidAmount')}</th>
                                <th className="px-2 py-2 text-center border-b border-black">{t('work.duration')}</th>
                                <th className="px-2 py-2 text-center border-b border-black">{t('work.orderDate')}</th>
                                <th className="px-2 py-2 text-center border-b border-black">{t('work.startDate')}</th>
                                <th className="px-2 py-2 text-center border-b border-black">{t('work.endDate')}</th>
                                <th className="px-2 py-2 text-center border-b border-black">{t('dashboard.work')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="11" className="text-center p-4">{t('common.loading')}</td></tr>
                            ) : data.length === 0 ? (
                                <tr><td colSpan="11" className="text-center py-4">{t('common.noRecords')}</td></tr>
                            ) : (
                                data.map((item, idx) => (
                                    <tr key={item.id} className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-gray-100`}>
                                        <td className="px-2 py-2 text-center border-r">{page * size + idx + 1}</td>
                                        <td className="px-2 py-2 border-r font-medium uppercase">{item.workTitle}</td>
                                        <td className="px-2 py-2 border-r">{item.tenderId}</td>
                                        <td className="px-2 py-2 border-r font-semibold">{item.workOrderNo}</td>
                                        <td className="px-2 py-2 border-r">{item.bidderName}</td>
                                        <td className="px-2 py-2 border-r text-right font-bold">
                                            {item.bidAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-2 py-2 text-center border-r">{item.duration}</td>
                                        <td className="px-2 py-2 text-center border-r">{item.workOrderDate}</td>
                                        <td className="px-2 py-2 text-center border-r">{item.workStartDate}</td>
                                        <td className="px-2 py-2 text-center border-r">{item.workEndDate}</td>
                                        <td className="px-2 py-2 text-center">
                                            <button
                                                onClick={() => handleMarkComplete(item)}
                                                className="staff-btn-save"
                                            >
                                                {item.isCompleted ? t('dashboard.workCompleted') : "Completed"}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm">
                    <p>
                        {t('common.showing')} {data.length > 0 ? page * size + 1 : 0} {t('common.to')} {Math.min((page + 1) * size, totalElements)} {t('common.of')} {totalElements} {t('common.entries')}
                    </p>
                    <div className="flex gap-2">
                        <button
                            disabled={page === 0}
                            onClick={() => setPage(p => p - 1)}
                            className="px-3 py-1 border rounded disabled:opacity-50 bg-white text-blue-600 hover:bg-gray-50"
                        >
                            {t('common.previous')}
                        </button>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                className={`px-3 py-1 border rounded ${page === i ? 'bg-[#a0adf1] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                onClick={() => setPage(i)}
                            >
                                {i + 1}
                            </button>
                        )).slice(Math.max(0, page - 2), Math.min(totalPages, page + 3))}
                        <button
                            disabled={page >= totalPages - 1}
                            onClick={() => setPage(p => p + 1)}
                            className="px-3 py-1 border rounded disabled:opacity-50 bg-white text-blue-600 hover:bg-gray-50"
                        >
                            {t('common.next')}
                        </button>
                    </div>
                </div>
            </div>
        </PageFade>
    );
};

export default WorkCompletionList;